import {Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle} from '@tryghost/shade';
import {InviteTeamMemberPage} from './InviteTeamMemberPage';
import {LucideIcon, formatNumber} from '@tryghost/shade/utils';
import {TeamMemberTable} from '@/components/team/TeamMemberTable';
import {useEffect, useMemo, useRef, useState} from 'react';
import {useTeamMembers} from '@/hooks/useTeamMembers';
import type {MouseEvent} from 'react';

type TeamFilter = 'all' | 'active' | 'pending';

function EmptyState({onInvite}: {onInvite: (event: MouseEvent<HTMLButtonElement>) => void}) {
    return (
        <div className="studio-team-empty">
            <span aria-hidden="true" className="studio-team-empty-icon"><LucideIcon.UsersRound size={24} /></span>
            <h2>No team members yet</h2>
            <p>Invite someone and choose which publications they can access.</p>
            <button className="studio-control studio-control-primary" type="button" onClick={onInvite}>
                <LucideIcon.Plus size={16} />
                Invite member
            </button>
        </div>
    );
}

function LoadingSkeleton() {
    return (
        <div aria-label="Loading team members" className="studio-team-grid" role="status">
            {['first', 'second', 'third'].map(key => (
                <div key={key} className="studio-team-card-skeleton">
                    <span />
                    <div><span /><span /></div>
                </div>
            ))}
        </div>
    );
}

function ErrorState({message, onRetry}: { message: string; onRetry: () => void }) {
    return (
        <div className="studio-team-empty" role="alert">
            <div aria-hidden="true" className="studio-team-error-icon"><LucideIcon.CircleAlert size={26} /></div>
            <h2>Couldn’t load your team</h2>
            <p>{message}</p>
            <button className="studio-control studio-button-secondary" type="button" onClick={onRetry}>Try again</button>
        </div>
    );
}

export function TeamPage() {
    const {data: members = [], isLoading, isError, error, refetch} = useTeamMembers();
    const [filter, setFilter] = useState<TeamFilter>('all');
    const [query, setQuery] = useState('');
    const [showInviteDialog, setShowInviteDialog] = useState(false);
    const inviteTrigger = useRef<HTMLButtonElement | null>(null);
    const headerInviteTrigger = useRef<HTMLButtonElement | null>(null);
    const openInviteDialog = (event: MouseEvent<HTMLButtonElement>) => {
        inviteTrigger.current = event.currentTarget;
        setShowInviteDialog(true);
    };

    const activeCount = members.filter(member => member.status === 'active').length;
    const pendingCount = members.filter(member => member.status === 'pending').length;
    const newsletterCount = new Set(members.flatMap(member => member.sites.map(site => site.tenant_id))).size;
    const filteredMembers = useMemo(() => {
        const normalizedQuery = query.trim().toLowerCase();
        return members.filter((member) => {
            const matchesFilter = filter === 'all' || member.status === filter;
            const searchableText = [member.name, member.email, member.role, ...member.sites.map(site => site.site_name)].join(' ').toLowerCase();
            return matchesFilter && (!normalizedQuery || searchableText.includes(normalizedQuery));
        });
    }, [filter, members, query]);
    const clearFilters = () => {
        setFilter('all');
        setQuery('');
    };

    useEffect(() => {
        if (!showInviteDialog) {
            return;
        }

        document.body.classList.add('studio-create-dialog-open');
        return () => document.body.classList.remove('studio-create-dialog-open');
    }, [showInviteDialog]);

    return (
        <div className="studio-content-page studio-team-page">
            <header className="studio-page-heading studio-team-heading">
                <div className="studio-team-heading-copy">
                    <h1>Team</h1>
                    <p className="studio-team-intro">Manage members, invitations, roles, and publication access.</p>
                </div>
                <button ref={headerInviteTrigger} className="studio-control studio-control-primary studio-team-invite-button" type="button" onClick={openInviteDialog}>
                    <LucideIcon.UserRoundPlus size={17} />
                    Invite member
                </button>
            </header>

            {!isLoading && !isError && <div aria-label="Team overview" className="studio-team-summary">
                <span aria-label={`${formatNumber(members.length)} ${members.length === 1 ? 'member' : 'members'}`}><strong>{formatNumber(members.length)}</strong> {members.length === 1 ? 'member' : 'members'}</span>
                <span aria-label={`${formatNumber(activeCount)} active`}><strong>{formatNumber(activeCount)}</strong> active</span>
                <span aria-label={`${formatNumber(pendingCount)} pending`}><strong>{formatNumber(pendingCount)}</strong> pending</span>
                <span aria-label={`${formatNumber(newsletterCount)} ${newsletterCount === 1 ? 'newsletter' : 'newsletters'}`}><strong>{formatNumber(newsletterCount)}</strong> {newsletterCount === 1 ? 'newsletter' : 'newsletters'}</span>
            </div>}

            <section aria-labelledby="team-directory-title" className="studio-team-directory">
                <div className="studio-team-directory-heading">
                    <h2 id="team-directory-title">Members</h2>
                    {!isLoading && !isError && <label className="studio-team-search">
                        <LucideIcon.Search size={16} />
                        <input aria-label="Search team members" placeholder="Search people or publications" type="search" value={query} onChange={event => setQuery(event.target.value)} />
                    </label>}
                </div>

                {!isLoading && !isError && !!members.length && <div aria-label="Filter team members" className="studio-team-filters" role="group">
                    <button aria-pressed={filter === 'all'} type="button" onClick={() => setFilter('all')}>All <span>{formatNumber(members.length)}</span></button>
                    <button aria-pressed={filter === 'active'} type="button" onClick={() => setFilter('active')}>Active <span>{formatNumber(activeCount)}</span></button>
                    <button aria-pressed={filter === 'pending'} type="button" onClick={() => setFilter('pending')}>Pending <span>{formatNumber(pendingCount)}</span></button>
                </div>}

                {isLoading ? (
                    <LoadingSkeleton />
                ) : isError ? (
                    <ErrorState message={error instanceof Error ? error.message : 'Failed to load team members'} onRetry={() => refetch()} />
                ) : !members.length ? (
                    <EmptyState onInvite={openInviteDialog} />
                ) : !filteredMembers.length ? (
                    <div className="studio-team-no-results">
                        <LucideIcon.SearchX size={24} />
                        <h3>No matching team members</h3>
                        <p>Try a different search or status filter.</p>
                        <button type="button" onClick={clearFilters}>Clear filters</button>
                    </div>
                ) : (
                    <TeamMemberTable members={filteredMembers} />
                )}
            </section>

            <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
                <DialogContent className="studio-active studio-create-dialog studio-team-invite-dialog" onCloseAutoFocus={(event) => {
                    event.preventDefault();
                    (inviteTrigger.current?.isConnected ? inviteTrigger.current : headerInviteTrigger.current)?.focus();
                }}>
                    <DialogClose asChild>
                        <button aria-label="Close invite team member" className="studio-create-dialog-close" type="button">
                            <LucideIcon.X size={18} />
                        </button>
                    </DialogClose>
                    <DialogHeader className="studio-create-dialog-heading">
                        <DialogTitle>Invite a team member</DialogTitle>
                        <DialogDescription>Choose who can collaborate on your newsletters.</DialogDescription>
                    </DialogHeader>
                    <InviteTeamMemberPage modal onSuccess={() => setShowInviteDialog(false)} />
                </DialogContent>
            </Dialog>
        </div>
    );
}
